import React, { useEffect, useState } from "react"
import { Link } from "gatsby"
import tw from "twin.macro"

import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import { buttonGreenViolet } from "@styles/ui"

import ModalExit from "@components/ModalExit"

import { InitLoadHook } from "@utils/GetInitLoad"
import { useLockScroll } from "@utils/LockScroll"
import { isEDMode } from "@utils/Utils"

import LogoDark from "@images/shared/logo_defencath-dark.inline.svg"
import LogoLight from "@images/shared/logo_defencath-light.inline.svg"
import Close from "@images/shared/icon_close-button.inline.svg"
import MobileMenuDark from "@images/shared/icon_mobile-menu-dark.inline.svg"
import MobileMenuLight from "@images/shared/icon_mobile-menu-light.inline.svg"
import Arrow from "@images/shared/icon_arrow-right.inline.svg"
import Schema from "@components/Schema"

const Header = ({
  colorTheme = "purple",
  sticky,
  location,
  headerRef,
  legalPage,
}) => {
  const [theme, setTheme] = useState(colorTheme)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { lockScroll, unlockScroll } = useLockScroll()

  const [screenshotMode, setScreenshotMode] = useState(false)
  const [lockedScreen, setLockedScreen] = useState(false)

  const initLoad = InitLoadHook()

  const isHome = location.pathname === "/"

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const scrollToIsi = () => {
    document.querySelector("#main-isi").scrollIntoView({ behavior: "smooth" })
    document.querySelector("#main-isi").focus({ preventScroll: true })
  }

  useEffect(() => {
    if (!isEDMode && isHome) {
      const scrollHandler = () => {
        const sectionTwo = document.querySelector("#sectionTwo")
        const safetySection = document.querySelector(".safety")
        const sectionThreePartTwo = document.querySelector(
          "#sectionThreePartTwo"
        )
        if (
          ScrollTrigger.positionInViewport(sectionThreePartTwo, "bottom") <=
          0.15
        ) {
          setTheme("reducedWhite")
        } else if (
          ScrollTrigger.positionInViewport(safetySection, "top") <= -0.75
        ) {
          setTheme("tan")
        } else if (
          ScrollTrigger.positionInViewport(sectionTwo, "bottom") <= 0.1
        ) {
          setTheme("purple")
        } else if (ScrollTrigger.positionInViewport(sectionTwo, "top") <= 0.1) {
          setTheme("white")
        } else {
          setTheme("purple")
        }
      }

      window.addEventListener("scroll", scrollHandler)

      return () => {
        window.removeEventListener("scroll", scrollHandler)
      }
    }
  }, [isHome])

  useEffect(() => {
    if (location?.search.includes("ed=1")) {
      setScreenshotMode(true)
    }
  }, [location?.search])

  useEffect(() => {
    if (mobileMenuOpen) {
      lockScroll()
      setLockedScreen(true)
    } else {
      unlockScroll()
      setTimeout(() => {
        setLockedScreen(false)
      }, 100)
    }
  }, [mobileMenuOpen, lockScroll, unlockScroll])

  const useScrollDirection = () => {
    const [scrollDirection, setScrollDirection] = useState("")
    const [prevOffset, setPrevOffset] = useState(0)

    const toggleScrollDirection = e => {
      let scrollY = window.scrollY
      let threshold = headerRef.current
        ? headerRef.current?.getBoundingClientRect().height
        : 0

      if (scrollY > prevOffset && scrollY > threshold && !lockedScreen) {
        setScrollDirection("down")
      } else if (scrollY <= prevOffset && scrollY > threshold) {
        setScrollDirection("up")
      } else {
        setScrollDirection("")
      }
      setPrevOffset(scrollY)
    }

    useEffect(() => {
      if (!isHome) {
        window.addEventListener("scroll", toggleScrollDirection)
        return () => {
          window.removeEventListener("scroll", toggleScrollDirection)
        }
      }
    })

    return scrollDirection
  }
  const scrollDirection = useScrollDirection()

  const navLinks = [
    {
      title: (
        <>
          Proven
          <br aria-hidden="true" tw="hidden lg:(block)" /> Efficacy
        </>
      ),
      path: "/efficacy",
    },
    {
      title: (
        <>
          How&nbsp;DefenCath
          <br aria-hidden="true" tw="hidden lg:(block)" /> Works
        </>
      ),
      path: "/how-defencath-works",
    },
    {
      title: <>Safety</>,
      path: "/safety",
    },
    {
      title: (
        <>
          Dosing and Administration
        </>
      ),
      path: "/dosing-and-administration",
    },
    {
      title: (
        <>
          Ordering
          <br aria-hidden="true" tw="hidden lg:(block)" /> DefenCath
        </>
      ),
      path: "/ordering-defencath",
    },
  ]

  const themeColor = {
    link: ({ theme }) => [
      theme === "purple" && tw`lg:(text-purple)`,
      (theme === "white" || theme === "reducedWhite") && tw`lg:(text-white)`,
      theme === "tan" && tw`lg:(text-tan)`,
    ],
    eyebrow: ({ theme }) => [
      (theme === "white" || theme === "reducedWhite") &&
        tw`bg-white text-purple [a]:after:(bg-purple)`,
      theme === "tan" && tw`bg-tan text-purple`,
      theme === "purple" && tw`bg-purple text-white`,
    ],
    button: ({ theme }) => [
      (theme === "white" || theme === "reducedWhite") &&
        tw`lg:(bg-white text-purple)`,
      theme === "tan" && tw`lg:(bg-tan text-purple)`,
      theme === "purple" && tw`lg:(bg-purple text-tan)`,
    ],
    navBar: ({ theme }) => [
      (theme === "white" || theme === "reducedWhite" || theme === "tan") &&
        tw`lg:(bg-purple)`,
      theme === "purple" && tw`lg:(bg-tan)`,
    ],
    mainNav: ({ theme }) => [
      (theme === "white" || theme === "reducedWhite") &&
        tw`mobileOnly:(bg-white-gradient transition-all ease-in duration-500)`,
      theme === "tan" &&
        tw`mobileOnly:(bg-tan-gradient transition-all ease-in duration-500)`,
      theme === "purple" &&
        tw`mobileOnly:(bg-purple-hp-gradient transition-all ease-in duration-500)`,
    ],
    border: ({ theme }) => [
      (theme === "white" || theme === "tan" || theme === "reducedWhite") &&
        tw`border-white lg:(hocus:border-white)`,
      theme === "purple" && tw`border-purple lg:(hocus:border-purple)`,
    ],
  }

  const screenshotsStyles = {
    navLinks: ({ location }) => [
      screenshotMode &&
        location?.search.includes("menuHover=1") &&
        tw`lg:(border-b-purple) [span]:lg:(-translate-y-1)`,
      screenshotMode &&
        location?.search.includes("menuActive=1") &&
        tw`lg:(border-b-violet text-violet)`,
    ],
    navButton: ({ location }) =>
      screenshotMode &&
      location?.search.includes("menuHover=1") &&
      tw`lg:(bg-green text-purple)`,
    navPanel: screenshotMode && tw`absolute`,
    navBackground: ({ location }) =>
      screenshotMode &&
      mobileMenuOpen &&
      location?.search.includes("mobileMenu=1") &&
      tw`bg-tan h-[100dvh] md:(h-auto)`,
    skipToContent: ({ location }) =>
      screenshotMode &&
      location?.search.includes("skipToContent=1") &&
      tw`translate-y-4`,
  }

  return (
    <div
      data-screenshot="header"
      css={screenshotsStyles.navBackground({ location })}
    >
      {isHome && <Schema />}
      <a
        css={[
          buttonGreenViolet,
          tw`absolute top-0 left-1/2 -translate-x-1/2 z-40 -translate-y-full transition-all duration-700`,
          tw`focus:translate-y-4`,
          screenshotsStyles.skipToContent({ location }),
        ]}
        href="#main-content"
      >
        Skip to main content
      </a>
      <header
        css={[
          tw`fixed w-full top-0 z-30 transition-all duration-700 -translate-y-full bg-[linear-gradient(179deg, #240041 29.12%, rgba(36, 0, 65, 0.81) 56.87%, rgba(36, 0, 65, 0.46) 76.81%, rgba(36, 0, 65, 0.00) 92.98%)] lg:(bg-transparent bg-none)`,
          isHome &&
            (theme === "white" ||
              theme === "tan" ||
              theme === "reducedWhite") &&
            tw`bg-transparent bg-none`,
          initLoad && tw`translate-y-0`,
          scrollDirection === "up" && tw`translate-y-0 top-0`,
          scrollDirection === "down" && tw`-translate-y-full top-0`,
          screenshotMode && tw`absolute`,
        ]}
        className="header"
        ref={headerRef}
      >
        {/* For screenshots */}
        <ModalExit
          screenshotMode={true}
          targetModal="modal-exit"
          triggerContent="screenshot exit modal"
          triggerStyles={[tw`hidden`]}
          destinationUrl="#"
        />
        {/* EYEBROW */}
        <div
          data-id="eyebrow"
          css={[
            tw`transition-all duration-500 text-sm leading-3 py-1 px-5 bg-purple text-white border-b-[0.5px] border-purple lg:(text-2xl pt-2 px-0 border-0)`,
            themeColor.eyebrow({ theme }),
            location.pathname !== "/" &&
              scrollDirection !== "" &&
              tw`transition-all duration-500 lg:(bg-transparent)`,
            legalPage && tw`lg:(bg-transparent)`,
            mobileMenuOpen && tw`transition duration-500 bg-tan text-purple`,
          ]}
        >
          <div tw="max-w-[82rem] mx-auto lg:(flex justify-between px-4) 2xl:(px-0)">
            <div tw="mb-1">
              <p tw="font-bold">For US healthcare professionals only.</p>
            </div>
            <div>
            <ModalExit
                triggerContent={"DefenCath Reimbursement Support"}
                triggerStyles={[
                  tw`pr-2 font-bold relative hover:text-green lg:(pr-2 text-lg) text-[11px]`,
                ]}
                destinationUrl="https://www.defencathsupport.com/"
              />
            </div>
            <div>
              
              {/* &nbsp;|&nbsp; */}
              <a
                css={[tw`pl-2 pr-2 font-bold relative hover:text-green lg:(pr-4) lg:(pr-2 text-lg) text-[11px]`]}
                href="/pdfs/prescribing-information.pdf"
                target="_blank"
                rel="noopener noreferrer"
              ><strong>Prescribing Information</strong>
               
              </a>
              {/* <span
                css={[
                  tw`inline-block bg-white w-[1px] h-2 absolute top-[1.4rem] lg:(h-[1.1875rem] top-1.5)`,
                  mobileMenuOpen && tw`bg-purple`,
                  isHome &&
                    (theme === "white" ||
                      theme === "tan" ||
                      theme === "reducedWhite") &&
                    tw`bg-purple`,
                ]}
              ></span> */}
              {/* <button
                data-gtm="anchor-nav"
                onClick={scrollToIsi}
                tw="pl-2 font-bold hover:text-green lg:(pl-4)"
                rel="noreferrer"
              >
                Important Safety Information
              </button> */}
            </div>
          </div>
        </div>

        {/* MAIN NAV */}
        <div data-id="main-nav">
          <nav
            css={[
              tw`max-w-[82rem] mx-auto bg-transparent w-full lg:(flex justify-between items-center px-4) 2xl:(px-0)`,
            ]}
          >
            <div
              css={[
                tw`transition duration-500 py-1 flex justify-between pt-2 px-4 pb-4 lg:(px-0 py-0)`,
                mobileMenuOpen && tw`transition bg-tan duration-500`,
              ]}
            >
              {/* LOGO */}
              <div data-id="logo">
                <Link
                  aria-label="homepage"
                  tw="block relative w-[240px] xl:(w-[320px])"
                  to="/"
                >
                  <LogoDark
                    css={[
                      tw`hidden w-full`,
                      (theme === "white" ||
                        theme === "tan" ||
                        theme === "reducedWhite" ||
                        mobileMenuOpen) &&
                        tw`block transition-all duration-500`,
                    ]}
                    role="img"
                    aria-label="DefenCath logo"
                  />
                  <LogoLight
                    css={[
                      tw`hidden w-full`,
                      theme === "purple" &&
                        !mobileMenuOpen &&
                        tw`block transition-all duration-500`,
                    ]}
                    role="img"
                    aria-label="DefenCath logo"
                  />
                </Link>
              </div>

              {/* MOBILE MENU TRIGGER */}
              <div data-id="mobile-menu-open" tw="lg:(hidden)">
                <button
                  css={[
                    tw`transition-all visible`,
                    mobileMenuOpen && tw`transition-all invisible`,
                  ]}
                  tabIndex={mobileMenuOpen ? -1 : null}
                  aria-label="open mobile menu"
                  onClick={toggleMobileMenu}
                  data-id="mobile-menu-button"
                >
                  <MobileMenuDark
                    css={[
                      tw`hidden`,
                      (theme === "white" ||
                        theme === "tan" ||
                        theme === "reducedWhite") &&
                        tw`block`,
                    ]}
                  />
                  <MobileMenuLight
                    css={[tw`hidden`, theme === "purple" && tw`block`]}
                  />
                </button>
              </div>
            </div>
            <div
              className="nav-panel"
              css={[
                tw`z-40 bg-tan fixed left-[100%]  w-full invisible transition-all duration-500`,
                `
              top: ${headerRef.current?.offsetHeight}px;
              height: calc(100vh - ${headerRef.current?.offsetHeight}px);
              height: calc(100dvh - ${headerRef.current?.offsetHeight}px);
            `,
                mobileMenuOpen &&
                  tw`left-0 visible transition-all duration-500`,
                tw`lg:(h-auto relative visible left-0 top-0 bg-transparent w-auto)`,
                screenshotsStyles.navPanel,
              ]}
            >
              <div
                className="menu-wrapper"
                tw="h-full lg:(h-auto flex items-center justify-between mt-4)"
              >
                <button
                  data-id="mobile-menu-close"
                  tw="z-10 absolute right-4 -top-[4.2rem] lg:(hidden)"
                  aria-label="close mobile navigation menu"
                  onClick={toggleMobileMenu}
                >
                  <Close />
                </button>

                {/* NAV LINKS */}
                <div
                  data-id="nav-links"
                  css={[
                    tw`px-6 h-full flex flex-col lg:(pl-10 pr-1.5 flex-row gap-x-6 items-center h-auto rounded-[100px]) xl:(gap-x-8) 2xl:(pl-20 gap-x-10) 2xl:(gap-x-16)`,
                    themeColor.navBar({ theme }),
                    tw`transition-all duration-500`,
                  ]}
                >
                  {navLinks.map((l, index) => {
                    return (
                      <Link
                        key={`nav-link-${index}`}
                        className="group"
                        aria-current={
                          location?.pathname === l.path ? "page" : null
                        }
                        css={[
                          themeColor.border({ theme }),
                          tw`transition-all block font-kanit tracking-[0.005rem] font-semibold border-b px-8 py-[3.25vh] lg:(text-xs flex items-center min-h-[68px]) xl:(text-sm leading-tight min-h-[71px])`,
                          tw`sm:(py-8)`,
                          tw`lg:(relative px-0 py-[18px] border-0)`,
                          themeColor.link({ theme }),
                          index === navLinks.length - 1 && tw`border-0`,
                          location?.pathname === l.path &&
                            tw` bg-green lg:(text-violet bg-transparent)`,
                        ]}
                        to={l.path}
                      >
                        <span
                          css={[
                            tw`block transition duration-500 translate-y-0 lg:(group-hocus:(-translate-y-1))`,
                            location?.pathname === l.path &&
                              tw`lg:(group-hocus:(translate-y-0))`,
                          ]}
                        >
                          {l.title}
                        </span>
                        <span
                          css={[
                            tw`hidden lg:(block duration-500 absolute w-full h-1 bg-transparent transition bottom-0 group-hocus:bg-violet)`,
                            location?.pathname === l.path && tw`lg:(bg-violet)`,
                          ]}
                        />
                      </Link>
                    )
                  })}
                  <ModalExit
                    triggerContent={
                      <>
                        <span tw="mr-2 [line-height: 1] lg:(text-left inline-block)">
                          DefenCath <br tw="hidden lg:block" />
                          Resource Portal
                        </span>
                        <Arrow tw="inline" />
                      </>
                    }
                    triggerStyles={[
                      tw`flex items-center bg-purple text-white justify-center w-full text-sm py-4 pl-8 pr-4 font-kanit tracking-[0.005rem] font-semibold rounded-[100px] mt-auto mb-8 transition duration-300 lg:(my-auto text-xs pr-4 py-4 pl-8 hover:(bg-green text-purple)) xl:(text-sm)`,
                      themeColor.button({ theme }),
                      screenshotsStyles.navButton({ location }),
                    ]}
                    destinationUrl="https://www.defencathresources.com/"
                  />
                </div>
              </div>
            </div>
          </nav>
        </div>
        <div
          css={[
            tw`-z-[1] absolute w-full h-full block top-6 left-0 transition-all duration-500 pointer-events-none`,
            tw`bg-[linear-gradient(180deg, #240041 22.6%, #240041cc 65.29%, #24004199, #24004166, #24004100)]`,
            tw`lg:(bg-[linear-gradient(180deg, #240041 22.6%, rgba(36, 0, 65, 0.57) 65.29%, rgba(36, 0, 65, 0.00) 95.39%)] top-0 h-[180px])`,
            isHome &&
              theme === "white" &&
              tw`bg-[linear-gradient(180deg, #ffffff 22.6%, #fffffff0 65.29%, #ffffffd0, #ffffff75, #ffffff00)] lg:(bg-[linear-gradient(180deg, #ffffff 22.6%, #fffffff5 65.29%, #ffffffbd, #ffffff00)]) `,
            isHome &&
              theme === "tan" &&
              tw`bg-[linear-gradient(180deg, #f3e9da 22.6%, #f3e9daf7 65.29%, #f3e9dae0, #f3e9da75, #f3e9da00)] lg:(bg-[linear-gradient(180deg, #f3e9da 22.6%, #f3e9daf0 65.29%, #f3e9da9c, #f3e9da00)]) `,
            isHome &&
              theme === "reducedWhite" &&
              tw`h-[80%] bg-[linear-gradient(180deg, #ffffff 22.6%, #fffffff0 65.29%, #ffffffd0, #ffffff75, #ffffff00)] lg:(h-[145px] bg-[linear-gradient(180deg, white 50%, rgba(255,255,255,1) 70%, rgba(255,255,255,0.9) 80%, rgba(255,255, 255,0))])`,
            !isHome && tw`opacity-100`,
            !isHome && scrollDirection === "down" && tw`opacity-0`,
          ]}
        ></div>
      </header>
    </div>
  )
}

export default Header
